import moment from './moment';

export default {
  mixins: [ moment ],
  props: {
    value: {
      type: [String, Date, Object, Array],
      default: null,
    },
    format: {
      type: String,
      default: 'DD.MM.YYYY',
    },
    defaultDisplayDate: {
      type: [String, Date, Object],
      default: null,
    },
    range: {
      type: Boolean,
      default: false,
    },
    events: {
      type: Array,
      default: () => [],
      validator: (value) => value?.every((event) => event && event.date),
    },
    holidays: {
      type: Array,
      default: () => [],
      validator: (value) => value?.every((holiday) => holiday && holiday.date),
    },
    disabledDates: {
      type: Array,
      default: () => [],
      validator: (value) => value?.every((item) => {
        return item && (typeof item === 'string' || item instanceof Date || (typeof item === 'object' && item.date));
      }),
    },
    availableDates: {
      type: Array,
      default: () => [],
      validator: (value) => value?.every((item) => {
        return item && (typeof item === 'string' || item instanceof Date || (typeof item === 'object' && item.date));
      }),
    },
    disabledPastDates: {
      type: Boolean,
      default: false,
    },
    minDate: {
      type: [String, Date, Object],
      default: null,
    },
    disabledFutureDates: {
      type: Boolean,
      default: false,
    },
    startYear: {
      type: Number,
      default: null,
    },
    endYear: {
      type: Number,
      default: null,
    },
    maxRangeDays: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      currentDate: this.calcCurrentDate(),
      selectedDate: null,
      startDate: null,
      endDate: null,
      selectingRange: false,
      dayHeaders: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'],
      selectedYear: this.calcCurrentDate().year(),
      showingYears: false,
      showingMonths: false,
    };
  },
  computed: {
    monthName() {
      return this.formatMoment(this.currentDate, 'MMMM');
    },
    yearOptions() {
      const years = [];
      let minYear;
      let maxYear;

      if (this.startYear !== null && this.endYear !== null) {
        minYear = this.startYear;
        maxYear = this.endYear;
      } else {
        const baseYear = this.getYearMoment(this.currentDate);
        minYear = baseYear - 20;
        maxYear = baseYear + 20;
      }

      for (let year = minYear; year <= maxYear; year++) {
        years.push(year);
      }
      return years;
    },
    /**
     * Generates an array of localized month names (e.g., 'Січень', 'Лютий').
     * This replaces the hardcoded monthNames array in data.
     * @returns {Array<string>}
     */
    monthNames() {
      const locale = 'uk-UA';
      const formatter = new Intl.DateTimeFormat(locale, { month: 'long' });
      const months = [];
      for (let i = 0; i < 12; i++) {
        const date = new Date(2000, i, 1);
        months.push(formatter.format(date));
      }
      return months;
    },
    calendarDates() {
      const today = this.getMoment();
      const startOfMonth = this.startOfMoment(this.currentDate, 'month');
      const endOfMonth = this.endOfMoment(this.currentDate, 'month');
      const startOfWeek = this.startOfMoment(startOfMonth, 'week');
      const endOfWeek = this.endOfMoment(endOfMonth, 'week');

      const dates = [];
      let current = this.cloneMoment(startOfWeek);

      while (this.isSameOrBeforeMoment(current, endOfWeek, 'day')) {
        const dateObj = {
          date: this.cloneMoment(current),
          day: this.getDateMoment(current),
          isCurrentMonth: this.isSameMoment(current, this.currentDate, 'month'),
          isToday: this.isSameMoment(current, today, 'day'),
          key: this.formatMoment(current, 'YYYY-MM-DD'),
          hasEvents: this.hasEventsOnDate(current),
          isHoliday: this.isHoliday(current),
          isWeekend: this.isWeekend(current),
          isDisabled: this.isDisabledDate(current),
        };

        if (this.range) {
          dateObj.isSelected =
            (this.startDate && this.isSameMoment(current, this.startDate, 'day')) ||
            (this.endDate && this.isSameMoment(current, this.endDate, 'day'));

          dateObj.isRangeStart = this.startDate && this.isSameMoment(current, this.startDate, 'day');
          dateObj.isRangeEnd = this.endDate && this.isSameMoment(current, this.endDate, 'day');

          dateObj.inRange =
            this.startDate &&
            this.endDate &&
            this.isSameOrBeforeMoment(this.startDate, current, 'day') &&
            this.isSameOrBeforeMoment(current, this.endDate, 'day');
        } else {
          dateObj.isSelected =
            this.selectedDate && this.isSameMoment(current, this.selectedDate, 'day');
        }

        dates.push(dateObj);
        current = this.addMoment(current, 1, 'day');
      }

      return dates;
    },
  },
  methods: {
    calcCurrentDate() {
      let momentDate;
      if (this.defaultDisplayDate && !this.value) {
        momentDate = this.getMoment(this.defaultDisplayDate, this.format);
      } else if (this.value) {
        const val = Array.isArray(this.value) ? this.value[0] : this.value;
        momentDate = this.getMoment(val, this.format);
      } else {
        momentDate = this.getMoment();
      }

      if (this.endYear) {
        momentDate.year(Math.min(momentDate.year(), this.endYear));
      }

      return momentDate;
    },
    calcSelectedYear() {
      if (this.defaultDisplayDate && !this.value) {
        const y = this.getMoment(this.defaultDisplayDate, this.format).year();
        return this.endYear ? Math.min(y, this.endYear) : y;
      }
      if (this.value) {
        const val = Array.isArray(this.value) ? this.value[0] : this.value;
        const y = this.getMoment(val, this.format).year();
        return this.endYear ? Math.min(y, this.endYear) : y;
      }
      const y = this.getMoment().year();
      return this.endYear ? Math.min(y, this.endYear) : y;
    },
    hasEventsOnDate(date) {
      return this.events.some((event) =>
        this.isSameMoment(date, event.date, 'day')
      );
    },
    isHoliday(date) {
      return this.holidays.some((holiday) =>
        this.isSameMoment(date, holiday.date, 'day')
      );
    },
    isWeekend(date) {
      const day = this.getMoment(date).day();
      return day === 0 || day === 6;
    },
    getDaysDifference(startMoment, endMoment) {
      const d1 = this.toDateMoment(startMoment);
      const d2 = this.toDateMoment(endMoment);
      const diffTime = Math.abs(d2.getTime() - d1.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays + 1;
    },
    isDateExplicitlyDisabled(date) {
      if (this.availableDates.length > 0) {
        const isAvailable = this.availableDates.some((availableDateItem) => {
          const comparableDate = availableDateItem.date || availableDateItem;
          return this.isSameMoment(date, comparableDate, 'day');
        });
        return !isAvailable;
      }

      const today = this.getMoment();

      if (this.disabledPastDates && this.isBeforeMoment(date, today, 'day')) {
        return true;
      }

      if (this.disabledFutureDates && this.isAfterMoment(date, today, 'day')) {
        return true;
      }

      return this.disabledDates.some((disabledDateItem) => {
        const comparableDate = disabledDateItem.date || disabledDateItem;
        return this.isSameMoment(date, comparableDate, 'day');
      });
    },
    isDisabledDate(date) {
      if (this.isDateExplicitlyDisabled(date)) {
        return true;
      }

      if (this.minDate) {
        const minMoment = this.getMoment(this.minDate, this.format);
        if (minMoment.isValid() && this.isBeforeMoment(date, minMoment, 'day')) {
          return true;
        }
      }

      if (this.range && this.selectingRange && this.maxRangeDays !== null && this.startDate) {
        let tempStartDate = this.startDate;
        let tempEndDate = date;

        if (this.isAfterMoment(tempStartDate, tempEndDate, 'day')) {
          [tempStartDate, tempEndDate] = [tempEndDate, tempStartDate];
        }

        const daysInPotentialRange = this.getDaysDifference(tempStartDate, tempEndDate);
        return daysInPotentialRange > this.maxRangeDays;
      }

      return false;
    },
    isDisabledWeek(weekStartDate) {
      for (let i = 0; i < 7; i++) {
        const dayInWeek = this.addMoment(this.cloneMoment(weekStartDate), i, 'days');
        if (this.isDateExplicitlyDisabled(dayInWeek)) {
          return true;
        }
      }

      if (this.minDate) {
        const minMoment = this.getMoment(this.minDate, this.format);
        if (minMoment.isValid()) {
          const weekEnd = this.endOfMoment(this.cloneMoment(weekStartDate), 'week');
          if (this.isBeforeMoment(weekEnd, minMoment, 'day')) {
            return true;
          }
        }
      }

      if (this.range && this.selectingRange && this.startDate) {
        if (this.isBeforeMoment(weekStartDate, this.startDate, 'week')) {
          return true;
        }

        if (this.maxRangeDays !== null) {
          let testStart = this.startDate;
          let testEnd = this.endOfMoment(this.cloneMoment(weekStartDate), 'week');

          if (this.isAfterMoment(testStart, testEnd, 'day')) {
            testStart = this.cloneMoment(weekStartDate);
            testEnd = this.startDate;
          }

          const daysInPotentialRange = this.getDaysDifference(testStart, testEnd);
          if (daysInPotentialRange > this.maxRangeDays) {
            return true;
          }
        }
      }
      return false;
    },
    isDisabledMonth(monthDate) {
      if (!this.range || !this.selectingRange || this.maxRangeDays === null || !this.startDate) {
        return false;
      }

      const startOfMonth = this.startOfMoment(monthDate, 'month');
      const endOfMonth = this.endOfMoment(monthDate, 'month');

      if (this.isAfterMoment(this.startDate, endOfMonth, 'day')) {
        return true;
      }

      let testStart = this.startDate;
      let testEnd = endOfMonth;

      if (this.isAfterMoment(testStart, testEnd, 'day')) {
        testStart = startOfMonth;
        testEnd = this.startDate;
      }

      const daysInPotentialRange = this.getDaysDifference(testStart, testEnd);

      return daysInPotentialRange > this.maxRangeDays;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (this.range) {
          if (Array.isArray(newValue) && newValue.length === 2) {
            if (newValue[1] === null || typeof newValue[1] === 'undefined') {
              this.startDate = this.getMoment(newValue[0], this.format);
              this.endDate = null;
              this.currentDate = this.cloneMoment(this.startDate);
              this.selectedYear = this.getYearMoment(this.startDate);
              this.selectingRange = true;
            } else {
              this.startDate = this.getMoment(newValue[0], this.format);
              this.endDate = this.getMoment(newValue[1], this.format);
              this.currentDate = this.cloneMoment(this.startDate);
              this.selectedYear = this.getYearMoment(this.startDate);
              this.selectingRange = false;
            }
          } else if (newValue) {
            this.startDate = this.getMoment(newValue, this.format);
            this.endDate = null;
            this.currentDate = this.cloneMoment(this.startDate);
            this.selectedYear = this.getYearMoment(this.startDate);
            this.selectingRange = true;
          } else {
            this.startDate = null;
            this.endDate = null;
            this.selectingRange = false;
            this.currentDate = this.calcCurrentDate();
            this.selectedYear = this.calcSelectedYear();
          }
        } else {
          if (newValue) {
            this.selectedDate = this.getMoment(newValue, this.format);
            this.currentDate = this.cloneMoment(this.selectedDate);
            this.selectedYear = this.getYearMoment(this.selectedDate);
          } else {
            this.selectedDate = null;
            this.currentDate = this.calcCurrentDate();
            this.selectedYear = this.calcSelectedYear();
          }
        }
      },
    },
    selectedYear(newYear) {
      if (this.getYearMoment(this.currentDate) !== newYear) {
        this.currentDate = this.setYearMoment(this.currentDate, newYear);
      }
    },
    currentDate(newDate) {
      const newYear = this.getYearMoment(newDate);
      if (this.selectedYear !== newYear) {
        this.selectedYear = newYear;
      }
    },
  },
};
